import labels from '@/translations/labels.json'
import * as HELPERS from '@/utils/helpers'
import { storeToRefs } from 'pinia'
import { onMounted, Ref, watch } from 'vue'

import notifMessage from '@/translations/notifMessage.json'

import { GraphLayout } from './../rappid/graph-layout'
import GraphState from './../rappid/graph-state'
import MouseWheelController from './../rappid/mousewheel-controller'
import PanController from './../rappid/pan-controller'

import { useSnapshotStore } from '@/stores/snapshotStore'
import { useTraceDataStore } from '@/stores/traceDataStore'

import { useHelpers } from './helpers'
import { useRegisterArtifactContainerMenu } from './register-artifact-container-menu'
import { useRegisterArtifactTypeContainerMenu } from './register-artifact-type-container-menu'
import { useRegisterArtifactTypeContainerSearch } from './register-artifact-type-container-search'
import { useRegisterContainerCollapse } from './register-container-collapse'
import { useRegisterElementClickEvent } from './register-element-click-event'
import { useRegisterElementDoubleClickEvent } from './register-element-double-click-event'
import { useRegisterHighlighting } from './register-highlighting'
import { useRegisterLinkClickEvent } from './register-link-click-event'
import { useRegisterLoadArtifactsAfterExpand } from './register-load-artifacts-after-expand'
import { useRegisterLoadMorePill } from './register-load-more-pill'

import { useEventBus } from '@/utils/events'

import { injectTyped } from '@/inject'
import { Artifact, asSnapshotReference } from '@/models'
import { useRegisterElementRightClickEvent } from '@/pages/explorer/composables/register-element-right-click-event'
import { ServiceKeys } from '@/services/symbols'
import { parseIntUndefined } from '@/utils/helpers'
import { useRoute } from 'vue-router'

export function useRegisterExplorer(canvas: Ref<HTMLDivElement>) {
  const route = useRoute()

  const snapshotStore = useSnapshotStore()
  const traceDataStore = useTraceDataStore()

  const { getArtifactWithID, updatePropertiesWidget } = useHelpers()

  const { currentSnapshot } = storeToRefs(snapshotStore)

  const rappidContext = injectTyped(ServiceKeys.rappidContext)
  const artifactService = injectTyped(ServiceKeys.artifact)

  // The graph state object is held only here
  let graphState: GraphState | undefined = undefined

  // Create the rappid context before everything else, especially during setup and not onMounted.
  // The Rappid Context needs to be ready for the Minimap's onMounted hook
  rappidContext.create()

  // Use basic composables with lifecycle hooks
  useRegisterHighlighting()
  useRegisterContainerCollapse()

  // Use composables to register rappid/jointjs events
  const { registerLinkClickEvent } = useRegisterLinkClickEvent()
  const { registerElementClickEvent } = useRegisterElementClickEvent()
  const { registerElementDoubleClickEvent } = useRegisterElementDoubleClickEvent()
  const { registerElementRightClickEvent } = useRegisterElementRightClickEvent()

  const { registerArtifactTypeContainerMenu } = useRegisterArtifactTypeContainerMenu()
  const { registerArtifactContainerMenu } = useRegisterArtifactContainerMenu()

  const { registerLoadMorePill } = useRegisterLoadMorePill()
  const { registerLoadArtifactsAfterExpand } = useRegisterLoadArtifactsAfterExpand()
  const { registerArtifactTypeContainerSearch } = useRegisterArtifactTypeContainerSearch()

  // Update the graph state after new trace data from the API has been fetched
  useEventBus('traceModelStateChanged', () => {
    graphState?.addToModel(traceDataStore.modelState).layout()
  })

  useEventBus('searchedArtifacts', async (artifacts) => {
    if (artifacts.length > 0) {
      await filterExplorer(artifacts)
      graphState?.layout()
    } else {
      graphState?.clear()
      traceDataStore.resetModelState()
      await renderOverview()
      graphState?.layout()
    }
  })

  // Watch route changes to completely re-render the trace graph on changes
  watch(
    () => [
      route.params.projectId,
      route.params.snapshotId,
      route.params.artifactId,
      route.query.filter,
    ],
    async () => {
      graphState?.clear()

      traceDataStore.setSnapshotId(route.params.snapshotId as string)
      traceDataStore.setArtifactId(route.params.artifactId as string)

      if (snapshotStore.currentSnapshot !== undefined) {
        if (route.params.artifactId) {
          await renderOverview()
          await addDataToOverview()
        } else if (route.params.artifactId === undefined) {
          if (graphState === undefined) {
            graphState = new GraphState(
              rappidContext.graph,
              new GraphLayout(rappidContext),
              rappidContext,
            )
          } else {
            graphState.clear()
            traceDataStore.resetModelState()
          }
          await renderOverview()
        }
      }
    },
    { immediate: true },
  )

  // Watch snapshot change to re-render on page reload
  watch(currentSnapshot, async (newSnapshotValue, oldSnapshotValue) => {
    if (newSnapshotValue && oldSnapshotValue === undefined) {
      await renderOverview()
      if (route.params.artifactId) {
        await addDataToOverview()
        graphState?.layout()
      }
    }
  })

  onMounted(async () => {
    traceDataStore.setSnapshotId(route.params.snapshotId as string)

    if (graphState === undefined) {
      graphState = new GraphState(
        rappidContext.graph,
        new GraphLayout(rappidContext),
        rappidContext,
      )
    } else {
      graphState.clear()
      traceDataStore.resetModelState()
    }

    registerLinkClickEvent()

    registerArtifactContainerMenu()
    registerArtifactTypeContainerMenu()

    registerElementClickEvent()
    registerElementDoubleClickEvent()
    registerElementRightClickEvent()

    registerLoadMorePill(graphState)
    registerLoadArtifactsAfterExpand(graphState)
    registerArtifactTypeContainerSearch(graphState)

    if (snapshotStore.currentSnapshot !== undefined) {
      await renderOverview()
      if (route.params.artifactId) {
        await addDataToOverview()
      }
    }
  })

  async function filterExplorer(artifacts: Artifact[]): Promise<void> {
    try {
      if (!route.params.projectId || !route.params.snapshotId) {
        throw Error(labels.EXPLORER.ERRORS.OVERVIEW_FAILED)
      }

      rappidContext.mount(canvas.value)
      const selectionController = new PanController(rappidContext)
      selectionController.activate()

      const mouseController = new MouseWheelController(rappidContext)
      mouseController.activate()

      traceDataStore.setModelArtifacts(artifacts)
      traceDataStore.setModelLinks([])
      await renderOverview()
      await Promise.all(
        traceDataStore.modelState.artifacts.map(async (a: Artifact) => {
          await traceDataStore.setTraceDataFromApi({
            artifactService: artifactService,
            projectId: parseInt(route.params.projectId as string),
            snapshotId: asSnapshotReference(route.params.snapshotId),
            filterId: parseIntUndefined(route.query.filter as string),
            id: a.id,
            direction: 'any',
          })
        }),
      )

      // Get the links for the resulting artifacts
      await Promise.all(
        traceDataStore.modelState.artifacts.map(async (a: Artifact) => {
          await traceDataStore.updateTraceDataFromApi({
            artifactService: artifactService,
            projectId: parseInt(route.params.projectId as string),
            snapshotId: asSnapshotReference(route.params.snapshotId),
            filterId: parseIntUndefined(route.query.filter as string),
            id: a.id,
            direction: 'any',
            excludeArtifacts: true,
          })
        }),
      )

      await graphState?.filterModel(traceDataStore.modelState).layout()
    } catch {
      HELPERS.showTopAlertMessage(true, 'error', notifMessage.ERROR_ALERTS.SOMETHING_WENT_WRONG)
    }
  }

  async function renderOverview(): Promise<void> {
    try {
      if (!route.params.projectId || !route.params.snapshotId) {
        throw Error(labels.EXPLORER.ERRORS.OVERVIEW_FAILED)
      }

      await traceDataStore.loadTraceDataOverviewFromApi({
        artifactService: artifactService,
        projectId: parseInt(route.params.projectId as string),
        snapshotId: asSnapshotReference(route.params.snapshotId),
        filterId: parseIntUndefined(route.query.filter as string),
        id: parseInt(route.params.artifactId as string) || 0,
        direction: 'any',
      })

      rappidContext.mount(canvas.value)

      const selectionController = new PanController(rappidContext)
      selectionController.activate()

      const mouseController = new MouseWheelController(rappidContext)
      mouseController.activate()

      if (traceDataStore.modelState.linkTypes) {
        graphState
          ?.initializeOverview(
            traceDataStore.modelState.artifactTypes,
            traceDataStore.modelState.linkTypes,
          )
          .layout()
      }
    } catch {
      HELPERS.showTopAlertMessage(true, 'error', notifMessage.ERROR_ALERTS.SOMETHING_WENT_WRONG)
    }
  }

  async function addDataToOverview(): Promise<void> {
    if (!graphState || !route.params.projectId || !route.params.snapshotId) {
      throw Error('Missing data for rendering tracegraph')
    }
    await traceDataStore.addTraceDataToOverview({
      artifactService: artifactService,
      projectId: parseInt(route.params.projectId as string),
      snapshotId: asSnapshotReference(route.params.snapshotId),
      id: parseInt(route.params.artifactId as string),
      direction: 'any',
      filterId: parseIntUndefined(route.query.filter as string),
    })

    graphState?.addToModel(traceDataStore.modelState).layout()

    const currentArtifact = getArtifactWithID(
      route.params.artifactId as string,
      traceDataStore.modelState.artifacts,
    )
    updatePropertiesWidget(currentArtifact)
  }
}
